import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';

import cloneDeep from 'lodash/cloneDeep';
import qs from 'qs';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import { tableInitialState } from '../components/table-additional';
import createFilters from '../../shared/filters';
import { toggleOrderDirection } from '../../../../../shared/js/helpers';
import { createSelectOptionsFromSysEnums } from '../../shared/form-elements/select';

const filters = createFilters({
	namespace: 'programsAdditionalList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'locations_ids',
			title: 'Location',
			placeholder: 'Add location',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
			}
		},
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'tags',
			title: 'Tags',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFER_TAGS
			}
		},
		{
			name: 'status',
			title: 'Statuses',
			placeholder: 'Add statuses',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.offer-statuses'
			}
		},
		{
			name: 'priority',
			title: 'Priority',
			placeholder: 'Add priorities',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.offer-priorities'
			}
		},
		{
			name: 'status_1c',
			title: 'status_1c',
			placeholder: 'Add 1c status',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: 'v2/lookups/1c-statuses'
			}
		}
	]
});

// actions types
const TOGGLE_PROGRESS_BAR = 'programs/additional-list/toggle-progress-bar';
const FETCH_DATA_SUCCESS = 'programs/additional-list/fetch-data-success';
const FETCH_DATA_ERROR = 'programs/additional-list/fetch-data-error';
const REMOVE_SUCCESS = 'programs/additional-list/remove-success';
const REMOVE_ERROR = 'programs/additional-list/remove-error';
const SHOW_PROGRESS_BAR = 'programs/additional-list/show-progress-bar';
const CLEAR = 'programs/additional-list/clear';
const SET_SEARCH_QUERY = 'programs/additional-list/set-search-query';
const FETCH_MORE = 'programs/additional-list/fetch-more-success';
const SORT_BY_COLUMN = 'programs/additional-list/SORT_BY_COLUMN';

const SWITCH_TO_ORDER = 'programs/additional-list/switch-to-order';
const ON_ORDER_SORT_END = 'programs/additional-list/on-order-sort-end';
const SAVE_ORDER_SUCCESS = 'programs/additional-list/save-order-success';
const SAVE_ORDER_ERROR = 'programs/additional-list/save-order-error';

const FETCH_GEOS_SUCCESS = 'programs/additional-list/FETCH_GEOS_SUCCESS';
const FETCH_GEOS_ERROR = 'programs/additional-list/FETCH_GEOS_ERROR';

// permissions
export const allowedPermissions = {
	'view': 'offers_list',
	'edit': 'offers_create'
};

// initial state
const initialState = {
	data: null,
	table: tableInitialState,
	offerGeoPlans: [],
	showProgressBar: false,
	filters: filters.initialState,
	search: '',
	shouldSwitchToOrderMode: false,
	sortedOfferList: [],
	pagination: {
		limit: 150,
		offset: 0
	},
	collapseAllOffers: true,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			dispatch({ type: SHOW_PROGRESS_BAR });

			return request(getState, dispatch);
		},
		fetchMore: () => (dispatch, getState) => {
			return request(getState, dispatch);
		},
		sortByColumn: (col) => (dispatch, getState) => {
			const { sortedColumn } = selectors.getTable(getState());

			dispatch({
				type: SORT_BY_COLUMN,
				payload: { index: col, direction: toggleOrderDirection(sortedColumn.direction)}
			});

			return dispatch(actions.fetchData());
		},
		remove: id => dispatch => {
			const request = http({
				url: `${endpoints.OFFERS.BASE}/${id}`,
				method: 'delete',
				actions: [
					REMOVE_SUCCESS,
					REMOVE_ERROR
				]
			});

			dispatch(actions.toggleProgressBar());

			request(dispatch);
		},
		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());
			request(getState, dispatch);
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('offersSearchForm')(getState());
			const search = searchForm && searchForm.search || '';

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		showProgressBar: () => ({
			type: SHOW_PROGRESS_BAR
		}),

		apply: () => (dispatch, getState) => {
			dispatch(actions.showProgressBar());
			dispatch(actions.setSearchQuery());
			return request(getState, dispatch);
		},

		clear: () => dispatch => {
			dispatch(replace('programs/additional-list/'));
			dispatch({
				type: CLEAR
			});
		},
		toggleSwitchToOrder: () => ({
			type: SWITCH_TO_ORDER
		}),

		orderSortedEnd: sorted => ({
			type: ON_ORDER_SORT_END,
			payload: sorted
		}),

		loadGeosLookup: text => (dispatch, getState) => {
			const query = qs.stringify({query: text });
			const request = http({
				url: `v2/lookups/locations-with-traffic?${query}`,
				method: 'get',
				actions: [ FETCH_GEOS_SUCCESS, FETCH_GEOS_ERROR ]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_GEOS_SUCCESS && action.payload.data) {
					return {
						options: createSelectOptionsFromSysEnums(
							action.payload.data.map(item => ({ ...item, id: item.country_code }))
						)
					};
				}
			});
		},

		submitNewLocationPlan: formData => (dispatch, getState) => {
			const data = { ...formData, country_code: formData.country_code[0].value };
			return http({
				url: `v2/offers/locations-plan`,
				method: 'post',
				data,
				actions: [ FETCH_GEOS_SUCCESS, FETCH_GEOS_ERROR ]
			})(dispatch);
		},
		updateLocationPlan: formData => (dispatch, getState) => {
			const data = { ...formData, country_code: formData.country_code[0].value };
			return http({
				url: `v2/offers/locations-plan`,
				method: 'put',
				data,
				actions: [ FETCH_GEOS_SUCCESS, FETCH_GEOS_ERROR ]
			})(dispatch);
		},

		removeLocationPlan: (country_code) => (dispatch, getState) => {
			return http({
				url: `v2/offers/locations-plan/${country_code}`,
				method: 'delete',
				actions: [ FETCH_GEOS_SUCCESS ]
			})(dispatch);
		},

		saveOrderSort: () => (dispatch, getState) => {
			const sortedOfferList = getState().programsAdditionalList.sortedOfferList
				.map(row => ({
					additional_display_priority: row.sortIndex,
					location_id: JSON.parse(row.entityId).location_id,
					offer_id: JSON.parse(row.entityId).offer_id
				}));

			const request = http({
				url: endpoints.OFFERS.BASE.concat('/additional-list/sort'),
				method: 'put',
				actions: [SAVE_ORDER_SUCCESS, SAVE_ORDER_ERROR],
				data: sortedOfferList
			});

			dispatch(actions.showProgressBar());

			dispatch(request).then(action => {
				if (action.type === SAVE_ORDER_SUCCESS) {
					dispatch(actions.clear());
				}
			});
		}
	},
	filters.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return {
				...state,
				data: null
			};
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			let data = action.payload.data;
			data = data.map(row => {
				return row;
			});
			return {
				...state,
				data,
				showProgressBar: false,
				error: false
			};
		},
		[FETCH_MORE]: (state, action) => {
			const data = action.payload.data;
			return {
				...state,
				data: state.data.concat(data),
				showProgressBar: false,
				error: false
			};
		},
		[SORT_BY_COLUMN]: (state, action) => {
			return {
				...state, table: {
					...state.table,
					sortByColumn: { ... action.payload }
				}
			};
		},
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[REMOVE_SUCCESS]: state => ({...state, data: null, showProgressBar: false }),
		[REMOVE_ERROR]: state => ({...state, error: true}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload }),
		[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
		[CLEAR]: state => ({ ...initialState }),
		[SWITCH_TO_ORDER]: state => ({
			...state,
			shouldSwitchToOrderMode: !state.shouldSwitchToOrderMode
		}),
		[ON_ORDER_SORT_END]: (state, actions) => ({
			...state,
			sortedOfferList: actions.payload
		}),
		[SAVE_ORDER_SUCCESS]: state => ({
			...state,
			shouldSwitchToOrderMode: false,
			sortedOfferList: []
		})
	},
	filters.reducers
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.programsAdditionalList.data,
	getIntl: state => intl(state),
	getProgressBar: state => state.programsAdditionalList.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getTable: state => {
		const { table } = state.programsAdditionalList;
		const data = state.programsAdditionalList.data || [];
		return {
			headers: table.columns,
			rows: data,
			sortedColumn: table.sortByColumn,
			rowsNumber: table.rowsNumber,
			totalRows: data.length
		};
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.programsAdditionalList.filters,
		state => state
	)(state),
	getSearchInitialValues: state => ({
		search: state.programsAdditionalList.search
	}),
	getSwitchToOrderMode: state => state.programsAdditionalList.shouldSwitchToOrderMode,
	getShouldShowSaveOrderBtn: state => !!state.programsAdditionalList.sortedOfferList.length,
	getError: state => state.programsAdditionalList.error
};

const request = (getState, dispatch) => {
	const state = getState();
	const { filters: { selectedFilters }, search, pagination } = state['programsAdditionalList'];
	const filters = {};
	const { sortedColumn } = selectors.getTable(getState());

	selectedFilters.forEach(filter => {
		const values = filter.value.map(f => f.value);
		if (values.length) {
			filters[filter.name] = values;
		}
	});

	const params = {
		search: search,
		filters,
		pagination,
		sortBy: sortedColumn.index,
		sortDirection: sortedColumn.direction
	};

	return http({
		url: `${endpoints.OFFERS.BASE}/additional-list?${qs.stringify(params)}`,
		method: 'get',
		actions: [
			!pagination.offset ? FETCH_DATA_SUCCESS : FETCH_MORE,
			FETCH_DATA_ERROR
		],
		addToResponse: { params }
	})(dispatch);
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/programs/additional-list/index.js